import { Outlet, NavLink, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { RuoBanner } from "@/components/RuoBanner";
import api from "@/lib/api";
import "./admin-dark.css";
import {
  LayoutDashboard, Boxes, FileCheck2, ClipboardList, History, BookOpen, Search, Truck, LogOut, FlaskConical, PackageOpen, DollarSign, Bell, BadgePercent, Tag, ShieldCheck, Sparkles, CreditCard
} from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, test: "admin-nav-overview" },
  { to: "/admin/products", label: "Products", icon: Boxes, test: "admin-nav-products" },
  { to: "/admin/inventory", label: "Inventory", icon: PackageOpen, test: "admin-nav-inventory" },
  { to: "/admin/bookkeeping", label: "Bookkeeping", icon: DollarSign, test: "admin-nav-bookkeeping" },
  { to: "/admin/coas", label: "COAs / Batches", icon: FileCheck2, test: "admin-nav-coas" },
  { to: "/admin/coa-review", label: "COA Review Queue", icon: FileCheck2, test: "admin-nav-coa-review" },
  { to: "/admin/orders", label: "Orders & Review", icon: ClipboardList, test: "admin-nav-orders" },
  { to: "/admin/affiliates", label: "Affiliates", icon: BadgePercent, test: "admin-nav-affiliates" },
  { to: "/admin/promo-codes", label: "Promo Codes", icon: Tag, test: "admin-nav-promo-codes" },
  { to: "/admin/loyalty", label: "Loyalty Audit", icon: Sparkles, test: "admin-nav-loyalty" },
  { to: "/admin/scanner", label: "Claim Scanner", icon: Search, test: "admin-nav-scanner" },
  { to: "/admin/articles", label: "Articles", icon: BookOpen, test: "admin-nav-articles" },
  { to: "/admin/suppliers", label: "Suppliers", icon: Truck, test: "admin-nav-suppliers" },
  { to: "/admin/shipping", label: "Shipping", icon: Truck, test: "admin-nav-shipping" },
  { to: "/admin/payments", label: "Payment Methods", icon: CreditCard, test: "admin-nav-payments" },
  { to: "/admin/backup", label: "Backup & Restore", icon: ShieldCheck, test: "admin-nav-backup" },
  { to: "/admin/audit", label: "Audit Log", icon: History, test: "admin-nav-audit" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [badge, setBadge] = useState({ pending_review: 0, pending_crypto: 0, total: 0 });

  useEffect(() => {
    let alive = true;
    let prev = 0;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/notifications/badge");
        if (!alive) return;
        if (prev && data.total > prev) {
          // Soft chime via Web Audio so the admin hears new orders without dependencies
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = "sine"; o.frequency.value = 880;
            g.gain.setValueAtTime(0.0001, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
            o.start(); o.stop(ctx.currentTime + 0.5);
          } catch (e) { /* audio blocked — silently ignore */ }
        }
        prev = data.total;
        setBadge(data);
      } catch (e) { /* silently ignore polling errors */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div className="admin-dark min-h-screen flex flex-col bg-white">
      <RuoBanner />
      <div className="flex flex-1">
        <aside className="w-64 border-r border-[#D1D5DB] bg-[#F7F7F8] flex-shrink-0 hidden lg:block">
          <div className="flex items-center gap-3 px-5 py-5 border-b border-[var(--line)]">
            <Link to="/" className="flex items-center gap-3 flex-1 min-w-0">
              <div className="relative h-10 w-10 flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--terra)] via-[var(--violet)] to-[var(--magenta)]" />
                <div className="absolute inset-[3px] rounded-full bg-[var(--paper)] grid place-items-center overflow-hidden">
                  <div className="absolute inset-0 starfield" />
                  <span className="relative font-display font-bold text-[var(--terra)] text-[14px]">N</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-[14px] leading-none tracking-[0.04em]">NEBULA AMINOS</div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--terra)] mt-0.5">Compliance Console</div>
              </div>
            </Link>
            <NavLink
              to="/admin/orders"
              className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-[#D1D5DB] hover:border-[#003366] transition-colors"
              data-testid="admin-bell"
              title={`${badge.total} action${badge.total === 1 ? "" : "s"} required`}
            >
              <Bell className="h-4 w-4 text-[#003366]" />
              {badge.total > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#A30414] text-white text-[10px] font-mono font-bold flex items-center justify-center" data-testid="admin-bell-count">
                  {badge.total > 99 ? "99+" : badge.total}
                </span>
              )}
            </NavLink>
          </div>
          <nav className="py-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.test}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 ${
                    isActive ? "border-[#003366] bg-white text-[#003366] font-semibold" : "border-transparent text-[#111216] hover:bg-white"
                  }`
                }
              >
                <n.icon className="h-4 w-4" strokeWidth={1.6} /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="px-5 py-4 border-t border-[#D1D5DB] mt-auto">
            <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[#5E636E]">Signed in as</div>
            <div className="font-mono text-xs mt-1 truncate">{user?.email}</div>
            <button onClick={logout} className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#A30414] hover:underline" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </aside>
        <div className="flex-1 min-w-0">
          {/* Mobile nav */}
          <nav className="lg:hidden border-b border-[#D1D5DB] flex overflow-x-auto bg-[#F7F7F8]">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `px-4 py-2 text-xs font-mono uppercase tracking-wider whitespace-nowrap ${isActive ? "text-[#003366] font-semibold" : "text-[#111216]"}`}>{n.label}</NavLink>
            ))}
          </nav>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
